/**
 * InitializationModal Component
 *
 * 스프레드시트 자동 생성 및 초기화 모달
 *
 * 4가지 뷰 상태를 관리합니다:
 * 1. IdleView - 초기 화면 (2개 버튼: 새 생성 / 기존에 추가)
 * 2. RunningView - 진행 중 (진행률 표시)
 * 3. SuccessView - 성공 (Spreadsheet ID 표시 + 복사)
 * 4. ErrorView - 실패 (에러 메시지 + 재시도)
 */

import { useState } from 'react'
import { Modal } from './ui/Modal'
import { useToast } from './ui/Toast'
import {
  SpreadsheetInitializer,
  type InitProgress,
  type InitResult,
  type InitMode,
} from '@/core/services/SpreadsheetInitializer'
import type { GoogleSheetsClient } from '@/core/api/GoogleSheetsClient'
import type { AuthUser } from '@/core/auth/types'

/**
 * 뷰 상태
 */
type ViewState = 'idle' | 'running' | 'success' | 'error'

/**
 * 초기화 작업 타입
 */
type InitOperation = 'new' | 'existing'

/**
 * InitializationModal Props
 */
export interface InitializationModalProps {
  /** 모달 표시 여부 */
  isOpen: boolean
  /** 닫기 핸들러 */
  onClose: () => void
  /** Google Sheets 클라이언트 */
  client: GoogleSheetsClient
  /** 초기화 모드 (test/production) */
  mode: InitMode
  /** 현재 로그인한 사용자 */
  currentUser: AuthUser | null
}

/**
 * 스프레드시트 초기화 모달
 *
 * @example
 * ```tsx
 * <InitializationModal
 *   isOpen={showModal}
 *   onClose={() => setShowModal(false)}
 *   client={googleSheetsClient}
 *   mode="production"
 *   currentUser={user}
 * />
 * ```
 */
export function InitializationModal({
  isOpen,
  onClose,
  client,
  mode,
  currentUser,
}: InitializationModalProps) {
  const toast = useToast()
  // 뷰 상태
  const [viewState, setViewState] = useState<ViewState>('idle')

  // 진행 상황
  const [progress, setProgress] = useState<InitProgress | null>(null)

  // 결과
  const [result, setResult] = useState<InitResult | null>(null)

  // 현재 작업
  const [currentOperation, setCurrentOperation] = useState<InitOperation | null>(null)

  /**
   * 초기화 시작 (새 스프레드시트 생성)
   */
  const handleStartNew = async () => {
    setCurrentOperation('new')
    setViewState('running')
    setProgress(null)
    setResult(null)

    const initializer = new SpreadsheetInitializer(client, mode, currentUser)
    const userName = currentUser?.name || 'Unknown'
    const title = `우리 지갑 - ${userName}`

    const initResult = await initializer.initializeNewSpreadsheet(title, (prog) => {
      setProgress(prog)
    })

    setResult(initResult)
    setViewState(initResult.success ? 'success' : 'error')
  }

  /**
   * 초기화 시작 (기존 스프레드시트에 시트 추가)
   */
  const handleStartExisting = async () => {
    setCurrentOperation('existing')
    setViewState('running')
    setProgress(null)
    setResult(null)

    const initializer = new SpreadsheetInitializer(client, mode, currentUser)

    const initResult = await initializer.addSheetsToExisting((prog) => {
      setProgress(prog)
    })

    setResult(initResult)
    setViewState(initResult.success ? 'success' : 'error')
  }

  /**
   * 재시도
   */
  const handleRetry = () => {
    if (currentOperation === 'new') {
      handleStartNew()
    } else if (currentOperation === 'existing') {
      handleStartExisting()
    }
  }

  /**
   * 모달 닫기 (상태 초기화)
   */
  const handleClose = () => {
    setViewState('idle')
    setProgress(null)
    setResult(null)
    setCurrentOperation(null)
    onClose()
  }

  /**
   * Spreadsheet ID 복사
   */
  const handleCopySpreadsheetId = () => {
    if (result?.spreadsheetId) {
      navigator.clipboard.writeText(result.spreadsheetId)
      toast.success('Spreadsheet ID가 클립보드에 복사되었습니다!')
    }
  }

  /**
   * 스프레드시트 열기
   *
   * 생성된 스프레드시트를 새 탭에서 엽니다.
   */
  const handleOpenSpreadsheet = () => {
    if (!result?.spreadsheetId) return

    const url = `https://docs.google.com/spreadsheets/d/${result.spreadsheetId}/edit`
    window.open(url, '_blank', 'noopener,noreferrer')
  }

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title="스프레드시트 초기화"
      size="md"
      showCloseButton={viewState !== 'running'}
      closeOnOverlayClick={viewState !== 'running'}
      closeOnEsc={viewState !== 'running'}
    >
      {/* IdleView: 초기 화면 */}
      {viewState === 'idle' && (
        <div className="space-y-6">
          <p className="text-gray-700 dark:text-gray-200">
            Google Sheets 스프레드시트와 필요한 시트들을 자동으로 생성하고 기본 데이터를
            초기화합니다.
          </p>

          <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 dark:bg-primary-900/20 dark:border-primary-800">
            <h3 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">초기화 모드</h3>
            <p className="text-sm text-primary-800 dark:text-primary-200">
              {mode === 'test' ? (
                <>
                  <strong>Test 모드:</strong> 모든 Mock 데이터를 삽입합니다 (카테고리 50+개,
                  사용자 3명, 결제수단 10개, 예산 전체)
                </>
              ) : (
                <>
                  <strong>Production 모드:</strong> 필수 데이터만 삽입합니다 (전체 카테고리,
                  로그인한 사용자 1명)
                </>
              )}
            </p>
          </div>

          <div className="space-y-3">
            <h3 className="font-semibold text-gray-900 dark:text-gray-100">작업 선택</h3>

            {/* 새 스프레드시트 생성 */}
            <button
              onClick={handleStartNew}
              className="w-full p-4 border-2 border-primary-600 rounded-lg hover:bg-primary-50 dark:border-primary-500 dark:hover:bg-primary-900/20 transition-colors text-left"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-primary-600 dark:bg-primary-500 rounded-lg flex items-center justify-center text-white font-bold">
                  1
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">새 스프레드시트 생성</h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    완전히 새로운 Google Sheets 스프레드시트를 생성하고 모든 시트를
                    초기화합니다. 생성된 Spreadsheet ID를 .env 파일에 설정해야 합니다.
                  </p>
                </div>
              </div>
            </button>

            {/* 기존 스프레드시트에 시트 추가 */}
            <button
              onClick={handleStartExisting}
              className="w-full p-4 border-2 border-green-600 rounded-lg hover:bg-green-50 dark:border-green-500 dark:hover:bg-green-900/20 transition-colors text-left"
            >
              <div className="flex items-start">
                <div className="flex-shrink-0 w-10 h-10 bg-green-600 dark:bg-green-500 rounded-lg flex items-center justify-center text-white font-bold">
                  2
                </div>
                <div className="ml-4 flex-1">
                  <h4 className="font-semibold text-gray-900 dark:text-gray-100">
                    기존 스프레드시트에 시트 추가
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mt-1">
                    이미 설정된 SPREADSHEET_ID에 필요한 시트들을 확인합니다. 기존 시트는
                    유지하고, 없는 시트만 새로 추가합니다.
                  </p>
                </div>
              </div>
            </button>
          </div>

          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
            <p className="text-sm text-blue-800 dark:text-blue-300">
              💡 <strong>안내:</strong> 기존 시트와 데이터는 그대로 유지됩니다. 필요한 시트가
              없는 경우에만 새로 추가됩니다.
            </p>
          </div>
        </div>
      )}

      {/* RunningView: 진행 중 */}
      {viewState === 'running' && (
        <div className="space-y-6">
          <div className="text-center">
            <div className="inline-block w-16 h-16 border-4 border-primary-600 dark:border-primary-500 border-t-transparent rounded-full animate-spin" />
            <p className="mt-4 text-lg font-semibold text-gray-900 dark:text-gray-100">초기화 진행 중...</p>
          </div>

          {progress && (
            <>
              {/* 진행률 바 */}
              <div>
                <div className="flex justify-between text-sm text-gray-600 dark:text-gray-300 mb-2">
                  <span>{progress.message}</span>
                  <span>{progress.percentage}%</span>
                </div>
                <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-3 overflow-hidden">
                  <div
                    className="bg-primary-600 dark:bg-primary-500 h-full rounded-full transition-all duration-300"
                    style={{ width: `${progress.percentage}%` }}
                  />
                </div>
              </div>

              {/* 현재 시트 정보 */}
              {progress.currentSheet && (
                <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 dark:bg-primary-900/20 dark:border-primary-800">
                  <p className="text-sm text-primary-900 dark:text-primary-100">
                    <strong>현재 작업:</strong> {progress.currentSheet}
                  </p>
                  <p className="text-sm text-primary-800 dark:text-primary-200 mt-1">
                    진행: {progress.completedSheets} / {progress.totalSheets} 시트 완료
                  </p>
                </div>
              )}

              {/* 단계별 상태 */}
              <div className="space-y-2">
                <div className="text-sm font-medium text-gray-700 dark:text-gray-200 mb-3">초기화 단계</div>
                {[
                  { step: 'preparing', label: '1. 준비 중' },
                  { step: 'creating_spreadsheet', label: '2. 스프레드시트 생성/확인' },
                  { step: 'checking_sheets', label: '3. 기존 시트 확인' },
                  { step: 'backing_up_sheets', label: '4. 시트 백업' },
                  { step: 'creating_sheets', label: '5. 시트 생성' },
                  { step: 'inserting_data', label: '6. 데이터 삽입' },
                  { step: 'finalizing', label: '7. 마무리' },
                ].map(({ step, label }) => {
                  const isCompleted = getStepIndex(progress.step) > getStepIndex(step)
                  const isCurrent = progress.step === step

                  return (
                    <div key={step} className="flex items-center space-x-2">
                      {isCompleted ? (
                        <svg
                          className="w-5 h-5 text-green-600"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                      ) : isCurrent ? (
                        <div className="w-5 h-5 border-2 border-primary-600 dark:border-primary-500 border-t-transparent rounded-full animate-spin" />
                      ) : (
                        <div className="w-5 h-5 border-2 border-gray-300 dark:border-gray-600 rounded-full" />
                      )}
                      <span
                        className={`text-sm ${
                          isCompleted
                            ? 'text-green-600 dark:text-green-400 line-through'
                            : isCurrent
                              ? 'text-primary-600 dark:text-primary-400 font-semibold'
                              : 'text-gray-500 dark:text-gray-400'
                        }`}
                      >
                        {label}
                      </span>
                    </div>
                  )
                })}
              </div>
            </>
          )}
        </div>
      )}

      {/* SuccessView: 성공 */}
      {viewState === 'success' && result && (
        <div className="space-y-6">
          {/* 성공 아이콘 */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full">
              <svg
                className="w-10 h-10 text-green-600 dark:text-green-400"
                fill="currentColor"
                viewBox="0 0 20 20"
              >
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">초기화 완료!</h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{result.message}</p>
          </div>

          {/* Spreadsheet ID (새 생성인 경우만) */}
          {result.spreadsheetId && (
            <div className="bg-primary-50 border border-primary-200 rounded-lg p-4 dark:bg-primary-900/20 dark:border-primary-800">
              <h4 className="font-semibold text-primary-900 dark:text-primary-100 mb-2">생성된 Spreadsheet ID</h4>
              <div className="flex items-center space-x-2 mb-3">
                <code className="flex-1 bg-white dark:bg-gray-800 px-3 py-2 rounded border border-primary-300 dark:border-primary-700 text-sm text-primary-900 dark:text-primary-100 font-mono break-all">
                  {result.spreadsheetId}
                </code>
                <button
                  onClick={handleCopySpreadsheetId}
                  className="px-4 py-2 bg-primary-600 dark:bg-primary-500 text-white rounded hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors whitespace-nowrap"
                  title="복사"
                >
                  복사
                </button>
              </div>

              {/* 스프레드시트 열기 버튼 */}
              <button
                onClick={handleOpenSpreadsheet}
                className="w-full px-4 py-2 bg-primary-600 hover:bg-primary-700 dark:bg-primary-500 dark:hover:bg-primary-600 text-white rounded-lg transition-colors font-medium flex items-center justify-center gap-2"
              >
                <span>🔗</span>
                <span>스프레드시트 열기</span>
              </button>

              <p className="text-sm text-primary-800 dark:text-primary-200 mt-3">
                이 ID를 <code className="bg-primary-200 dark:bg-primary-800 px-1 rounded">.env</code> 파일의{' '}
                <code className="bg-primary-200 dark:bg-primary-800 px-1 rounded">VITE_SPREADSHEET_ID</code>에
                설정하세요.
              </p>
            </div>
          )}

          {/* 기존 시트 정보 (유지됨) */}
          {result.existingSheets && result.existingSheets.length > 0 && (
            <div className="bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800 rounded-lg p-4">
              <h4 className="font-semibold text-green-900 dark:text-green-200 mb-2">
                ✅ 기존 시트 유지됨 ({result.existingSheets.length}개)
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {result.existingSheets.map((sheetName) => (
                  <li key={sheetName} className="text-sm text-green-800 dark:text-green-300">
                    {sheetName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 새로 생성된 시트 정보 */}
          {result.createdSheets && result.createdSheets.length > 0 && (
            <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 dark:text-blue-200 mb-2">
                ✨ 새로 추가된 시트 ({result.createdSheets.length}개)
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {result.createdSheets.map((sheetName) => (
                  <li key={sheetName} className="text-sm text-blue-800 dark:text-blue-300">
                    {sheetName}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* 백업된 시트 정보 */}
          {result.backedUpSheets && result.backedUpSheets.length > 0 && (
            <div className="bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 rounded-lg p-4">
              <h4 className="font-semibold text-yellow-900 dark:text-yellow-200 mb-2">
                백업된 시트 ({result.backedUpSheets.length}개)
              </h4>
              <ul className="list-disc list-inside space-y-1">
                {result.backedUpSheets.map((sheetName) => (
                  <li key={sheetName} className="text-sm text-yellow-800 dark:text-yellow-300">
                    {sheetName}
                  </li>
                ))}
              </ul>
              <p className="text-sm text-yellow-800 dark:text-yellow-300 mt-2">
                백업된 시트는 스프레드시트에서 확인할 수 있습니다.
              </p>
            </div>
          )}

          {/* 다음 단계 안내 */}
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">다음 단계</h4>
            <ol className="list-decimal list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
              {result.spreadsheetId && (
                <li>
                  생성된 Spreadsheet ID를 <code>.env</code> 파일에 설정
                </li>
              )}
              <li>애플리케이션을 새로고침하여 데이터 확인</li>
              <li>Google Sheets에서 스프레드시트를 열어 데이터 확인</li>
            </ol>
          </div>

          {/* 닫기 버튼 */}
          <button
            onClick={handleClose}
            className="w-full px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-semibold"
          >
            완료
          </button>
        </div>
      )}

      {/* ErrorView: 실패 */}
      {viewState === 'error' && result && (
        <div className="space-y-6">
          {/* 에러 아이콘 */}
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full">
              <svg className="w-10 h-10 text-red-600 dark:text-red-400" fill="currentColor" viewBox="0 0 20 20">
                <path
                  fillRule="evenodd"
                  d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                  clipRule="evenodd"
                />
              </svg>
            </div>
            <h3 className="mt-4 text-xl font-semibold text-gray-900 dark:text-gray-100">초기화 실패</h3>
            <p className="mt-2 text-gray-600 dark:text-gray-300">{result.message}</p>
          </div>

          {/* 에러 세부 정보 */}
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <h4 className="font-semibold text-red-900 dark:text-red-300 mb-2">에러 정보</h4>
            <p className="text-sm text-red-800 dark:text-red-300 whitespace-pre-wrap">{result.message}</p>
          </div>

          {/* 문제 해결 팁 */}
          <div className="bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-4">
            <h4 className="font-semibold text-gray-900 dark:text-gray-100 mb-2">문제 해결 팁</h4>
            <ul className="list-disc list-inside space-y-1 text-sm text-gray-700 dark:text-gray-200">
              <li>Google OAuth 로그인 상태를 확인하세요</li>
              <li>Google Sheets API 권한이 허용되어 있는지 확인하세요</li>
              <li>Spreadsheet ID가 올바르게 설정되어 있는지 확인하세요</li>
              <li>네트워크 연결 상태를 확인하세요</li>
            </ul>
          </div>

          {/* 버튼 */}
          <div className="flex gap-3">
            <button
              onClick={handleClose}
              className="flex-1 px-4 py-3 bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors font-semibold"
            >
              닫기
            </button>
            <button
              onClick={handleRetry}
              className="flex-1 px-4 py-3 bg-primary-600 dark:bg-primary-500 text-white rounded-lg hover:bg-primary-700 dark:hover:bg-primary-600 transition-colors font-semibold"
            >
              재시도
            </button>
          </div>
        </div>
      )}
    </Modal>
  )
}

/**
 * 단계 순서 인덱스 반환 (진행률 비교용)
 */
function getStepIndex(step: string): number {
  const steps = [
    'preparing',
    'creating_spreadsheet',
    'checking_sheets',
    'backing_up_sheets',
    'creating_sheets',
    'inserting_data',
    'finalizing',
    'completed',
  ]
  return steps.indexOf(step)
}
